import type { OperandDto, WhenDto } from "../../../../../types/resolver";
import { formatLiteral } from "./OperandBuilder/constants";

const COMPARATOR_SYMBOLS: Record<string, string> = {
    eq: "=",
    ne: "≠",
    gt: ">",
    gte: "≥",
    lt: "<",
    lte: "≤",
};

/** Context for resolving an answer value ID to display text (same question / same answer set). */
export interface AnswerDisplayContext {
    answerSetId?: string | number;
    questionId?: string | number;
}

/** Optional label resolvers so the equation shows question/answer labels instead of IDs. */
export interface WhenEquationOptions {
    getQuestionLabel?: (questionId: number | string) => string | undefined;
    getAnswerDisplayText?: (valueId: unknown, context: AnswerDisplayContext) => string | undefined;
}

/**
 * Renders an operand as a short equation token (e.g. key.code, answer("Question name"), 'literal').
 * When options provide getQuestionLabel / getAnswerDisplayText, uses labels instead of IDs.
 */
export function operandToEquation(
    op: OperandDto | null | undefined,
    options?: WhenEquationOptions,
    answerContext?: AnswerDisplayContext
): string {
    if (op == null) return "?";
    const type = op.operandType;
    if (type === "answer") {
        const id = op.questionId != null && op.questionId !== "" ? String(op.questionId) : "?";
        const label = options?.getQuestionLabel?.(op.questionId as number | string);
        if (label != null && label !== "") return `answer("${label.replace(/"/g, '\\"')}")`;
        return `answer(Q${id})`;
    }
    if (type === "derivedAttribute") {
        const name = op.derivedAttributeName ?? "?";
        return `derived.${name}`;
    }
    if (type === "key") {
        const key = op.key ?? "?";
        return `key.${key}`;
    }
    if (type === "workItemAttribute") {
        const name = op.workItemAttributeName ?? "?";
        return `workItem.${name}`;
    }
    if (type === "aggregate") {
        const operation = op.operation ?? "COUNT";
        const over = op.over ?? "codes";
        if (op.when) {
            const filter = whenToEquation(op.when, options);
            return `${operation}(${over}, ${filter})`;
        }
        return `${operation}(${over})`;
    }
    // Literal or unspecified: if we have answer context, try to resolve as answer display text
    if (answerContext && options?.getAnswerDisplayText) {
        const display = options.getAnswerDisplayText(op.value, answerContext);
        if (display != null && display !== "") return `'${display.replace(/'/g, "\\'")}'`;
    }
    const raw = formatLiteral(op.value);
    if (raw === "") return "?";
    if (typeof op.value === "string") return `'${raw.replace(/'/g, "\\'")}'`;
    return raw;
}

function getAnswerContext(op: OperandDto | null | undefined): AnswerDisplayContext | undefined {
    if (op?.operandType !== "answer") return undefined;
    if (op.answerSetId != null && op.answerSetId !== "") return { answerSetId: op.answerSetId, questionId: op.questionId };
    if (op.questionId != null && op.questionId !== "") return { questionId: op.questionId };
    return undefined;
}

/**
 * Renders a When condition as a single-line equation string (e.g. "key.code = 'PROCEDURE' AND (answer(\"Q name\") = 'Yes')").
 * Pass options with getQuestionLabel / getAnswerDisplayText to show question and answer labels instead of IDs.
 */
export function whenToEquation(when: WhenDto | null | undefined, options?: WhenEquationOptions): string {
    if (when == null) return "true";
    if (when.true === true) return "true";
    if (when.all) {
        const parts = when.all.conditions.map((c) => whenToEquation(c, options)).filter(Boolean);
        if (parts.length === 0) return "true";
        return parts.map((p) => `(${p})`).join(" AND ");
    }
    if (when.any) {
        const parts = when.any.conditions.map((c) => whenToEquation(c, options)).filter(Boolean);
        if (parts.length === 0) return "true";
        return parts.map((p) => `(${p})`).join(" OR ");
    }
    if (when.not) {
        const inner = whenToEquation(when.not.condition, options);
        return `NOT (${inner})`;
    }
    const compKeys = ["eq", "ne", "gt", "gte", "lt", "lte"] as const;
    for (const key of compKeys) {
        const comp = when[key];
        if (comp) {
            const sym = COMPARATOR_SYMBOLS[key];
            const answerCtx = getAnswerContext(comp.left);
            const left = operandToEquation(comp.left, options);
            const right = operandToEquation(comp.right, options, answerCtx);
            return `${left} ${sym} ${right}`;
        }
    }
    if (when.in) {
        const leftOp = when.in.left;
        const answerCtx = getAnswerContext(leftOp);
        const left = operandToEquation(leftOp, options);
        const right = (when.in.right ?? []).map((v) => {
            if (answerCtx && options?.getAnswerDisplayText) {
                const display = options.getAnswerDisplayText(v, answerCtx);
                if (display != null && display !== "") return `'${display.replace(/'/g, "\\'")}'`;
            }
            return typeof v === "string" ? `'${v.replace(/'/g, "\\'")}'` : formatLiteral(v);
        });
        return `${left} in [${right.join(", ")}]`;
    }
    if (when.startsWith) {
        const left = operandToEquation(when.startsWith.left, options);
        const prefix = when.startsWith.prefix ?? "";
        return `${left} starts with "${prefix.replace(/"/g, '\\"')}"`;
    }
    if (when.endsWith) {
        const left = operandToEquation(when.endsWith.left, options);
        const postfix = when.endsWith.postfix ?? "";
        return `${left} ends with "${postfix.replace(/"/g, '\\"')}"`;
    }
    if (when.contains) {
        const left = operandToEquation(when.contains.left, options);
        const substr = when.contains.substr ?? "";
        return `${left} contains "${substr.replace(/"/g, '\\"')}"`;
    }
    if (when.anyMatch) {
        const over = when.anyMatch.over ?? "?";
        const inner = whenToEquation(when.anyMatch.when, options);
        return `anyMatch(${over}, ${inner})`;
    }
    if (when.noneMatch) {
        const over = when.noneMatch.over ?? "?";
        const inner = whenToEquation(when.noneMatch.when, options);
        return `noneMatch(${over}, ${inner})`;
    }
    if (when.allMatch) {
        const over = when.allMatch.over ?? "?";
        const inner = whenToEquation(when.allMatch.when, options);
        return `allMatch(${over}, ${inner})`;
    }
    return "true";
}

/** One answer operand reference: question and/or answer set to load for labels. */
export interface AnswerRef {
    questionId?: number | string;
    answerSetId?: string | number;
}

/** Collects all answer operands from a when tree so we can fetch question/answer set labels. */
export function extractAnswerRefsFromWhen(when: WhenDto | null | undefined): AnswerRef[] {
    if (when == null) return [];
    const refs: AnswerRef[] = [];
    function addOp(op: OperandDto | null | undefined) {
        if (op?.operandType === "answer" && (op.questionId != null || op.answerSetId != null)) {
            refs.push({
                questionId: op.questionId,
                answerSetId: op.answerSetId != null && op.answerSetId !== "" ? op.answerSetId : undefined,
            });
        }
    }
    function walk(w: WhenDto | null | undefined) {
        if (w == null) return;
        if (w.all) w.all.conditions.forEach(walk);
        else if (w.any) w.any.conditions.forEach(walk);
        else if (w.not) walk(w.not.condition);
        else if (w.eq) {
            addOp(w.eq.left);
            addOp(w.eq.right);
        }
        else if (w.ne) {
            addOp(w.ne.left);
            addOp(w.ne.right);
        }
        else if (w.gt) {
            addOp(w.gt.left);
            addOp(w.gt.right);
        }
        else if (w.gte) {
            addOp(w.gte.left);
            addOp(w.gte.right);
        }
        else if (w.lt) {
            addOp(w.lt.left);
            addOp(w.lt.right);
        }
        else if (w.lte) {
            addOp(w.lte.left);
            addOp(w.lte.right);
        }
        else if (w.in) {
            addOp(w.in.left);
        }
        else if (w.startsWith) addOp(w.startsWith.left);
        else if (w.endsWith) addOp(w.endsWith.left);
        else if (w.contains) addOp(w.contains.left);
        else if (w.anyMatch) walk(w.anyMatch.when);
        else if (w.noneMatch) walk(w.noneMatch.when);
    }
    walk(when);
    return refs;
}
